import { ActionIcon, Alert, Button, Group, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconHistory, IconPencil, IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PartnerCurrentAccountPreview } from 'src/Components/BankAccounts/PartnerCurrentAccountPreview';
import { RecordBankAccountBalanceModal } from 'src/Components/Modals/RecordBankAccountBalanceModal';
import { BankLogo } from 'src/Components/Shared/BankLogo';
import useFetchBankAccounts from 'src/Hooks/Queries/useFetchBankAccounts';
import { useHasPermission } from 'src/Hooks/Shared/useHasPermission';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';

import styles from './BankAccountList.module.scss';

const BankAccountList = () => {
  const { t } = useTranslation();
  const navigate = useNavigateKeepingSearch();
  const { data: bankAccounts, isLoading } = useFetchBankAccounts();
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(bankAccounts);
  const { hasPermission: canRecordBalance } = useHasPermission('financeAdmin', 'full');
  const [recordingBankAccountId, setRecordingBankAccountId] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleAddAccountClick = () => {
    navigate('new-bank-account');
  };

  if (!isLoading && (!bankAccounts || bankAccounts.length === 0)) {
    return (
      <div className={styles.container}>
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title={t('bankAccounts.list.empty.title', 'Aucun compte bancaire')}
          color="gray"
          className={styles.alert}
        >
          {t(
            'bankAccounts.list.empty.message',
            'Créez un compte bancaire partagé pour suivre les dépenses communes plus facilement.'
          )}
        </Alert>
        <Button
          onClick={handleAddAccountClick}
          leftSection={<IconPlus size={20} />}
          variant="light"
          className={styles.addButton}
        >
          {t('bankAccounts.add', 'Ajouter un compte bancaire')}
        </Button>
        <PartnerCurrentAccountPreview />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <DataTable
        withRowBorders={false}
        style={{ width: '100%', minHeight: '10rem' }}
        records={sortedRecords ?? []}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        rowStyle={() => ({ height: 58 })}
        noRecordsText={t('bankAccounts.empty', 'Aucun compte bancaire')}
        fetching={isLoading}
        onRowClick={({ record }) => navigate(`bank-account/${record.id}`)}
        columns={[
          {
            accessor: 'name',
            title: t('bankAccounts.list.name', 'Compte'),
            sortable: true,
            render: ({ name, bankDomain, bankName }) => (
              <Group gap="sm">
                <BankLogo domain={bankDomain} name={bankName} size="sm" />
                <div className={styles.name}>{name}</div>
              </Group>
            ),
          },
          {
            accessor: 'bankName',
            title: t('bankAccounts.list.bank', 'Banque'),
            render: ({ bankName, bankDetail }) =>
              bankName ? `${bankName}${bankDetail ? ` · ${bankDetail}` : ''}` : '—',
          },
          {
            accessor: 'latestBalance.recordedAt',
            title: t('bankAccounts.list.lastStatement', 'Dernier solde'),
            render: ({ latestBalance }) =>
              latestBalance ? dayjs(latestBalance.recordedAt).format('DD/MM/YYYY') : '—',
          },
          {
            accessor: 'latestBalance.amount',
            title: t('bankAccounts.list.amount', 'Montant'),
            render: ({ latestBalance }) => {
              if (!latestBalance) return '—';
              return (
                <span className={latestBalance.amount < 0 ? styles.negativeAmount : undefined}>
                  {latestBalance.amount.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </span>
              );
            },
          },
          {
            accessor: 'actions',
            title: '',
            render: ({ id }) => {
              const historyLabel = t('bankAccounts.balance.history.view', "Voir l'historique");
              const recordLabel = t('bankAccounts.balance.record', 'Saisir un solde');
              const handleHistoryClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                navigate(`bank-account/${id}`);
              };
              const handleRecordClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                setRecordingBankAccountId(id);
              };
              return (
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  {isMobile ? (
                    <Tooltip label={historyLabel}>
                      <ActionIcon
                        variant="outline"
                        onClick={handleHistoryClick}
                        aria-label={historyLabel}
                      >
                        <IconHistory size={18} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="outline"
                      size="xs"
                      leftSection={<IconHistory size={16} />}
                      onClick={handleHistoryClick}
                    >
                      {historyLabel}
                    </Button>
                  )}
                  {canRecordBalance &&
                    (isMobile ? (
                      <Tooltip label={recordLabel}>
                        <ActionIcon
                          variant="outline"
                          onClick={handleRecordClick}
                          aria-label={recordLabel}
                        >
                          <IconPencil size={18} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <Button variant="outline" size="xs" onClick={handleRecordClick}>
                        {recordLabel}
                      </Button>
                    ))}
                </Group>
              );
            },
            textAlign: 'right',
          },
        ]}
      />
      <Button
        onClick={handleAddAccountClick}
        leftSection={<IconPlus size={20} />}
        variant="light"
        className={styles.addButton}
      >
        {t('bankAccounts.add', 'Ajouter un compte bancaire')}
      </Button>

      {recordingBankAccountId && (
        <RecordBankAccountBalanceModal
          opened
          bankAccountId={recordingBankAccountId}
          onClose={() => setRecordingBankAccountId(null)}
        />
      )}

      <PartnerCurrentAccountPreview />
    </div>
  );
};

export default BankAccountList;
